'use client';

import { useState, useEffect } from 'react';
import PersonalInfoSection from './PersonalInfoSection';
import AddressManagementSection from './AddressManagementSection';
import OrderHistorySection from './OrderHistorySection';
import SubscriptionManagementSection from './SubscriptionManagementSection';
import FamilyPreferencesSection from './FamilyPreferencesSection';
import NotificationSettingsSection from './NotificationSettingsSection';
import PaymentMethodsSection from './PaymentMethodsSection';
import LoyaltyRewardsSection from './LoyaltyRewardsSection';
import PrivacySupportSection from './PrivacySupportSection';
import Icon from '@/components/ui/AppIcon';

interface UserProfileInteractiveProps {
  personalInfo: any;
  addresses: any[];
  orders: any[];
  subscriptions: any[];
  familyPreferences: any;
  notificationSettings: any;
  paymentMethods: any[];
  loyaltyData: any;
}

const UserProfileInteractive = ({
  personalInfo,
  addresses,
  orders,
  subscriptions,
  familyPreferences,
  notificationSettings,
  paymentMethods,
  loyaltyData,
}: UserProfileInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-[60px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="h-96 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: 'UserCircleIcon' },
    { id: 'addresses', label: 'Addresses', icon: 'MapPinIcon' },
    { id: 'orders', label: 'Orders', icon: 'ClipboardDocumentListIcon' },
    { id: 'subscriptions', label: 'Subscriptions', icon: 'ArrowPathIcon' },
    { id: 'preferences', label: 'Preferences', icon: 'UserGroupIcon' },
    { id: 'notifications', label: 'Notifications', icon: 'BellIcon' },
    { id: 'payments', label: 'Payments', icon: 'CreditCardIcon' },
    { id: 'rewards', label: 'Rewards', icon: 'GiftIcon' },
    { id: 'privacy', label: 'Privacy & Support', icon: 'LockClosedIcon' },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-sm">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full">
            <Icon name="UserCircleIcon" size={32} className="text-primary" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-2xl text-foreground">{personalInfo.name}</h1>
            <p className="text-sm text-muted-foreground">{personalInfo.email}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-smooth whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground shadow-elevation-sm'
                  : 'bg-muted text-foreground hover:bg-border'
              }`}
            >
              <Icon name={tab.icon as any} size={16} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="animate-fade-in">
        {activeTab === 'personal' && <PersonalInfoSection initialData={personalInfo} />}
        {activeTab === 'addresses' && <AddressManagementSection addresses={addresses} />}
        {activeTab === 'orders' && <OrderHistorySection orders={orders} />}
        {activeTab === 'subscriptions' && <SubscriptionManagementSection subscriptions={subscriptions} />}
        {activeTab === 'preferences' && <FamilyPreferencesSection initialPreferences={familyPreferences} />}
        {activeTab === 'notifications' && <NotificationSettingsSection initialSettings={notificationSettings} />}
        {activeTab === 'payments' && <PaymentMethodsSection paymentMethods={paymentMethods} />}
        {activeTab === 'rewards' && (
          <LoyaltyRewardsSection totalPoints={loyaltyData.totalPoints} rewards={loyaltyData.rewards} />
        )}
        {activeTab === 'privacy' && <PrivacySupportSection />}
      </div>
    </div>
  );
};

export default UserProfileInteractive;